import { Button, Column, EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import type { OpeningHours } from 'lib-shared-types-api';
import { useCallback, useMemo, useState } from 'react';

import { ReviewConflictsModal } from 'components/settings/components/sections/openingHours/components/reviewConflictsModal/reviewConflictsModal';
import { getLocalDate } from 'components/settings/components/sections/openingHours/utils/date';
import {
  ListItemType,
  useScheduleContext,
} from 'components/settings/components/sections/openingHours/views/schedule/schedule.context';
import type { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';

import { validateIntervals } from '../../../../utils/validate';
import type { IntervalsSelectProps } from '../intervalsSelect/intervalsSelect';
import { ListHeader } from '../listHeader/listHeader';
import { ListItem } from '../listItem/listItem';
import { ListItemContext } from '../listItem/listItem.context';

import { AddExceptionModal } from './components/addExceptionModal/addExceptionModal';

type ModalType = 'add' | 'review';

const headers = [
  {
    label: 'Exceptions (optional)',
    subLabel: 'sorted by upcoming date',
  },
  {
    label: 'Custom audio messages (optional)',
    subLabel: 'sorted by upcoming date',
    helpText: `These replace the standard audio message. If set, customers will hear the audio
      messages when they call you outside the opening hours of the exception,
      e.g. during the holidays. This applies to all phone flows which use this schedule.`,
  },
  {
    label: 'Custom auto-replies (optional)',
    subLabel: 'sorted by upcoming date',
    helpText: `These replace the standard auto-reply. If set, customer will receive
      these auto-replies when they email you outside the opening hours of the exception,
      e.g. during the holidays. This applies to all email flows which use this schedule.`,
  },
];

export const ExceptionsList = (): JSX.Element => {
  const [activeModalType, setActiveModalType] = useState<null | ModalType>(
    null,
  );
  const [resolvedException, setResolvedException] =
    useState<null | OpeningHours>(null);

  const {
    intervalsErrors: { [ListItemType.Exception]: intervalsErrors },
    listItems: { [ListItemType.Exception]: listItems },
    setIntervalsErrors,
    setModifiedListItems,
  } = useScheduleContext();

  const handleValidateInterval = useCallback(
    (dayIndex): IntervalsSelectProps['validate'] =>
      (intervals) =>
        void setIntervalsErrors(
          ListItemType.Exception,
          validateIntervals({
            intervals,
            dayIndex,
            errors: intervalsErrors,
          }),
        ),
    [intervalsErrors, setIntervalsErrors],
  );

  const handleAddNewException = useCallback(
    (exception: OpeningHours) =>
      void setModifiedListItems(ListItemType.Exception, {
        [exception.id]: exception,
      }),
    [setModifiedListItems],
  );

  const handleIntervalsChange = useCallback(
    (id: string) => (intervals: OpeningHours['intervals']) => {
      const exception = listItems.find(
        ({ id: exceptionId }) => exceptionId === id,
      ) as OpeningHours;

      setModifiedListItems(ListItemType.Exception, {
        [id]: {
          ...exception,
          intervals,
        },
      });
    },
    [listItems, setModifiedListItems],
  );

  const handleAudioMessageChange = useCallback(
    (id: string) => (audioMessage?: AnnouncementItem) => {
      const exception = listItems.find((exc) => exc.id === id) as OpeningHours;

      setModifiedListItems(ListItemType.Exception, {
        [id]: {
          ...exception,
          announcement: audioMessage,
        },
      });
    },
    [listItems, setModifiedListItems],
  );

  const handleAutoReplyChange = useCallback(
    (id: string) => (autoReply?: string) => {
      const exception = listItems.find((exc) => exc.id === id) as OpeningHours;

      setModifiedListItems(ListItemType.Exception, {
        [id]: {
          ...exception,
          auto_reply_template_id: autoReply,
        },
      });
    },
    [listItems, setModifiedListItems],
  );

  const handleDelete = useCallback(
    (id: string) => () => {
      const exception = listItems.find((exc) => exc.id === id) as OpeningHours;

      setModifiedListItems(ListItemType.Exception, {
        [id]: {
          ...exception,
          archived: getLocalDate().toISOString(),
        },
      });
    },
    [listItems, setModifiedListItems],
  );

  const handleResolveConflicts = useCallback(
    (modifiedItems: Record<string, OpeningHours>) => {
      setModifiedListItems(ListItemType.Exception, modifiedItems);
      setResolvedException(null);
    },
    [setModifiedListItems],
  );

  const handleOverriddenChange = useCallback((listItem: OpeningHours) => {
    setResolvedException(listItem);
    setActiveModalType('review');
  }, []);

  const hasExceptions = !!listItems?.length;

  const context = useMemo(
    () => ({
      intervalsSelectProps: {
        errors: intervalsErrors,
        onChange: handleIntervalsChange,
        validate: handleValidateInterval,
      },
      onAudioMessageChange: handleAudioMessageChange,
      onAudioMessageClear: handleAudioMessageChange,
      onAutoReplyChange: handleAutoReplyChange,
      onAutoReplyClear: handleAutoReplyChange,
      onDelete: handleDelete,
      onOverriddenChange: handleOverriddenChange,
    }),
    [
      intervalsErrors,
      handleAudioMessageChange,
      handleAutoReplyChange,
      handleDelete,
      handleIntervalsChange,
      handleOverriddenChange,
      handleValidateInterval,
    ],
  );

  return (
    <>
      {hasExceptions ? (
        <>
          <ListHeader headers={headers}>
            <Button
              onClick={() => {
                setActiveModalType('add');
                ampli.businessHoursScheduleAddExceptionButtonClicked();
              }}
              variant="secondary"
            >
              Add exception
            </Button>
          </ListHeader>

          <ListItemContext.Provider value={context}>
            <Column.Row scrollable noVerticalPadding>
              <FlexBox flexDirection="column" gap="250" padding="0 0 300">
                {listItems.map((listItem, index) => (
                  <ListItem key={listItem.id} index={index} item={listItem} />
                ))}
              </FlexBox>
            </Column.Row>
          </ListItemContext.Provider>
        </>
      ) : (
        <Column.Row size={1}>
          <FlexBox height="100%" justifyContent="center" alignItems="center">
            <EmptyState
              buttonProps={{
                children: 'Add exception',
                variant: 'secondary',
                onClick: () => setActiveModalType('add'),
              }}
              description="Add exceptions for days when you’re closed or have different business hours!"
              icon="calendar-times"
              iconColor="neutral.700"
              title="Gone fishin’?"
            />
          </FlexBox>
        </Column.Row>
      )}

      {/* Modals */}
      {activeModalType === 'add' && (
        <AddExceptionModal
          exceptions={listItems}
          onModalClose={() => setActiveModalType(null)}
          onSubmit={handleAddNewException}
        />
      )}
      {activeModalType === 'review' && resolvedException && (
        <ReviewConflictsModal
          conflicts={listItems.reduce(
            (acc, curr) => ({
              ...acc,
              ...(curr.next_date === resolvedException.next_date && {
                [curr.id]: curr,
              }),
            }),
            {},
          )}
          onClose={() => setActiveModalType(null)}
          onResolve={handleResolveConflicts}
          resolvedDates={[resolvedException.next_date as string]}
        />
      )}
    </>
  );
};
